import { memo, useMemo } from 'react';
import notEmpty from '../../../core/util/notEmpty';
import type { ProductBackorder } from '../../queries/useProductBackorders';
import useProductBackorders from '../../queries/useProductBackorders';
import type { ProductWithRelatedData } from '../../types';
import ProductVariantsTable from '../ProductDetails/ProductTables/ProductVariantsTable';
import styles from './ProductListStockListTable.module.scss';

const ProductListStockListTable = ({
    product,
}: {
    product: ProductWithRelatedData;
}) => {
    const products = useMemo(
        () => [
            {
                ...product,
                variants: product.variants.map(item => ({
                    ...item,
                    isNew: false,
                    specificationAttributes: item.specificationAttributes || [],
                    customerPrice: item.customerPrice,
                    parent: undefined,
                })),
            },
        ],
        [product]
    );
    const productsSku = [
        product.sku,
        ...product.variants.map(item => item.sku),
    ].filter(notEmpty);

    const { data } = useProductBackorders(productsSku);

    const backordersItems = data
        ? productsSku
              .map(sku => {
                  const skuBackorders = data.filter(
                      pendingOrderedQuantity =>
                          pendingOrderedQuantity.sku === sku
                  );
                  if (skuBackorders.length) {
                      return { sku, backorders: skuBackorders };
                  }
                  return undefined;
              })
              .filter(notEmpty)
        : undefined;

    const backorders =
        backordersItems && backordersItems.length > 0
            ? backordersItems.reduce(
                  (result, item) => {
                      return {
                          ...result,
                          [item.sku]: item.backorders,
                      };
                  },
                  {} as Record<string, ProductBackorder[]>
              )
            : undefined;
    return (
        <div
            className={styles.productListStockListTable}
            data-test-class="stockListTable"
        >
            <ProductVariantsTable
                fixedWidth
                emphasizeSku
                hideImageColumn
                shouldShowVariantsColumn
                showAvailabilityMessage
                shouldShowSiblingsColumn={false}
                products={products}
                backorders={backorders}
            />
        </div>
    );
};

export default memo(ProductListStockListTable);
